import { Search } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import Header from "src/components/app/layout/Header";
import Input from "src/components/app/Input";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { getTopicsRequest } from "src/redux/slices/contentSlice";
import { getContentListRequest } from "src/redux/slices/contentSlice";
import Loader from "src/components/common/Loader";
import { Content } from "src/types/content.types";
import { Topic } from "src/types/content.types";
import Layout from "src/components/app/layout/Layout";
import FilterTabs from "src/components/common/Tabs/FilterTabs";
import analytics from "src/helpers/analytics";
import { EVENT_NAMES, SCREEN_NAMES } from "src/helpers/analytics/events";

const LearnContentListScreen: React.FC = () => {
  const [activeTopicIds, setActiveTopicIds] = useState<string[]>([]);
  const dispatch = useAppDispatch();
  const { topics } = useAppSelector((state) => state.content);
  const navigate = useNavigate();
  const [allContent, setAllContent] = useState<Content[]>([]);
  const params = useParams();
  const supportProgramKey = params["support-program-key"];
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [isLoadingInitial, setIsLoadingInitial] = useState<boolean>(false); // for search/filter
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false); // for pagination
  const [hasMore, setHasMore] = useState<boolean>(true);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Event: LEARN_CONTENT_LIST_SCREEN_LOADED
    analytics.pageView(SCREEN_NAMES.LEARN_CONTENT_LIST_SCREEN);
    getTopics();
    getContentList(1, searchQuery);
  }, []);

  useEffect(() => {
    initializeConteState();
    getContentList(1, searchQuery);
  }, [activeTopicIds]);

  useEffect(() => {
    if (page !== 1) {
      getContentList(page, searchQuery, true);
    }
  }, [page]);

  useEffect(() => {
    let prevScrollTop = 0;

    const handleScroll = () => {
      const scrollTop = document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      const isScrollingDown = scrollTop > prevScrollTop;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;

      if (
        isScrollingDown &&
        isNearBottom &&
        !isLoadingInitial &&
        !isLoadingMore &&
        hasMore
      ) {
        setPage((prev) => prev + 1);
      }

      prevScrollTop = scrollTop;
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isLoadingInitial, isLoadingMore, hasMore]);

  const initializeConteState = () => {
    setAllContent([]);
    setHasMore(true);
    setPage(1);
  };

  const getTopics = () => {
    new Promise((resolve, reject) => {
      dispatch(getTopicsRequest({ resolve, reject }));
    })
      .then((res) => {})
      .catch((err) => {
        console.error("err", err);
      });
  };

  const getContentList = (
    pageNum = 1,
    query = searchQuery,
    isNewPage = false
  ) => {
    if (isLoadingInitial || isLoadingMore) return;

    if (isNewPage) {
      setIsLoadingMore(true);
    } else {
      setIsLoadingInitial(true);
    }

    new Promise((resolve, reject) => {
      dispatch(
        getContentListRequest({
          resolve,
          reject,
          payload: {
            recommended_health_doctor: false,
            page: String(pageNum), // for pagination
            languages_id: [],
            search: query, // for search
            ...(activeTopicIds?.length && { topic_ids: activeTopicIds }), // for topic filter
          },
        })
      );
    })
      .then((res: unknown) => {
        const newContent = (res as { data?: Content[] })?.data || [];
        if (newContent.length === 0) setHasMore(false);
        if (isNewPage) {
          const combined = [...allContent, ...newContent];
          setAllContent(combined);
        } else {
          setAllContent(newContent);
        }
      })
      .finally(() => {
        setIsLoadingInitial(false);
        setIsLoadingMore(false);
      });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isLoadingInitial || isLoadingMore) return;
    const value = e.target.value;
    setSearchQuery(value);

    // Clear previous debounce timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Reset content before new search
    initializeConteState();

    // Set new debounce timer
    debounceTimer.current = setTimeout(() => {
      getContentList(1, value);
    }, 500); // adjust delay as needed
  };

  const handleTopicClick = (topicId: string) => {
    if (isLoadingInitial || isLoadingMore) return;
    if (activeTopicIds.includes(topicId)) {
      // Remove topic ID
      const newIds = activeTopicIds.filter((id) => id !== topicId);
      setActiveTopicIds(newIds);
    } else {
      // Add topic ID
      setActiveTopicIds([...activeTopicIds, topicId]);
    }
  };

  const navigateToContentDetailsScreen = (content: Content) => {
    // Event: EDUCATIONAL_CONTENT_LIST_ITEM_CLICKED
    analytics.trackEvent(
      `${content?.title?.replace(/ /g, "_")}_${
        EVENT_NAMES.EDUCATIONAL_CONTENT_LIST_ITEM_CLICKED
      }`,
      {
        screen_name: SCREEN_NAMES.LEARN_CONTENT_LIST_SCREEN,
        support_program_key: supportProgramKey,
        card_title: content?.title,
        topic_name: content?.topic_name,
      }
    );
    navigate(
      `/app/${supportProgramKey}/learn-content/${content?.content_master_id}`,
      {
        state: {
          content,
        },
      }
    );
  };

  return (
    <Layout supportProgramKey={supportProgramKey || ""}>
      <div className="relative pt-44">
        <div className="fixed top-0 left-0 right-0 z-10 max-w-lg mx-auto">
          <Header
            title="Educational Content"
            titlePosition="center"
            onBackClick={() => navigate(-1)}
          />
          <div className="p-4 space-y-4 bg-white border-b border-gray-200">
            <Input
              name="search"
              type="text"
              placeholder="Search by article name"
              fullWidth
              onChange={handleSearchChange}
              startAdornment={<Search className="w-4 h-4" />}
            />
            <FilterTabs
              tabs={
                topics?.data?.map((topic: Topic) => ({
                  id: topic?.topic_master_id,
                  label: topic?.name,
                })) || []
              }
              activeTabIds={activeTopicIds}
              onTabClick={handleTopicClick}
              isLoading={topics?.isLoading}
            />
          </div>
        </div>
        <div className="p-4 space-y-4">
          {/* Top search/filter loader */}
          {isLoadingInitial ? (
            <div className="flex justify-center items-center">
              <Loader size="lg" />
            </div>
          ) : Array.isArray(allContent) && allContent?.length ? (
            allContent?.map((content: Content) => (
              <div
                key={content.content_master_id}
                className="flex flex-col items-start gap-2 min-w-[200px] h-full overflow-hidden bg-white rounded-2xl"
                onClick={() => navigateToContentDetailsScreen(content)}
              >
                <div className="w-full h-[200px] object-cover">
                  <img
                    src={content?.media[0]?.home_thumbnail_url}
                    alt={content.title}
                    className="w-full h-full overflow-hidden object-cover"
                  />
                </div>
                <div className="flex flex-col items-start flex-wrap gap-2 p-3">
                  <div className="flex items-center flex-wrap gap-2">
                    {content?.topic_name?.split(",")?.map((tag: string) => (
                      <div
                        key={tag}
                        className="text-xs font-medium text-[var(--app-primary)] bg-[var(--icon-bg-primary)] text-center border border-[var(--app-primary)] px-2 py-1 rounded-full"
                      >
                        {tag}
                      </div>
                    ))}
                  </div>
                  <div className="text-xs font-medium text-black line-clamp-2">
                    {content.title}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div>No content found</div>
          )}

          {/* Bottom pagination loader */}
          {isLoadingMore && (
            <div className="flex justify-center py-4">
              <Loader size="lg" />
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default LearnContentListScreen;
